import React, { useState, useEffect } from 'react';
import { X, BookOpen, Printer, ExternalLink, Download } from 'lucide-react';
import { fetchDocumentsConfig, DEFAULT_DOCUMENTS_CONFIG } from '../../../utils/documentsConfig';

export default function DocumentViewerModal({ isOpen, onClose, documentId }) {
  const [docConfig, setDocConfig] = useState(DEFAULT_DOCUMENTS_CONFIG);

  useEffect(() => {
    if (isOpen) {
      fetchDocumentsConfig().then(cfg => setDocConfig(cfg));
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isOracleGuide = documentId === 'oracle-connection-guide' || documentId === 'candor-oracle-database-connection-guide';
  const currentConfig = isOracleGuide 
    ? (docConfig.oracleConnectionGuide || DEFAULT_DOCUMENTS_CONFIG.oracleConnectionGuide)
    : (docConfig.etlScriptGuide || DEFAULT_DOCUMENTS_CONFIG.etlScriptGuide);

  const documentTitle = currentConfig.title;
  const publicUrl = process.env.PUBLIC_URL || '';
  
  const rawUrl = currentConfig.url || (isOracleGuide ? '/documents/Oracle Database _ UserGuide.pdf' : '/documents/ETL_userguide.pdf');
  const pdfUrl = rawUrl.startsWith('http') ? rawUrl : `${publicUrl}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
  const pdfFilename = currentConfig.filename || (isOracleGuide ? 'Oracle Database _ UserGuide.pdf' : 'ETL_userguide.pdf');

  const handlePrint = () => {
    const iframe = document.getElementById('pdf-document-frame');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } else {
      window.open(pdfUrl, '_blank');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-900/80 backdrop-blur-sm animate-fade-in overflow-y-auto"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="document-modal-title"
        className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-5xl max-h-[92vh] h-[88vh] flex flex-col overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between shrink-0 border-b border-slate-800">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-lg shrink-0 ${isOracleGuide ? 'bg-blue-600/30 text-blue-400' : 'bg-violet-600/30 text-violet-400'}`}>
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-wider font-bold text-slate-400 block">
                Technical Documentation Guide
              </span>
              <h2 id="document-modal-title" className="text-lg sm:text-xl font-bold text-white truncate">
                {documentTitle}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-semibold"
              title="Open PDF in New Tab"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden sm:inline">Open New Tab</span>
            </a>
            <a
              href={pdfUrl}
              download={pdfFilename}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-semibold"
              title="Download PDF Document"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download</span>
            </a>
            <button
              onClick={handlePrint}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title="Print Document"
            >
              <Printer className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* PDF Viewer Container */}
        <div className="flex-1 bg-slate-100 p-2 sm:p-4 overflow-hidden relative flex flex-col">
          <iframe
            id="pdf-document-frame"
            src={`${pdfUrl}#toolbar=1&navpanes=0`}
            title={documentTitle}
            className="w-full h-full rounded-xl border border-slate-200 shadow-inner bg-white"
          />
        </div>

        {/* Modal Footer */}
        <div className="bg-white px-6 py-3 border-t border-slate-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 text-xs text-slate-600 font-medium truncate">
            <span className="font-semibold text-slate-900">{documentTitle}</span>
            <span className="text-slate-400">•</span>
            <span className="text-slate-500 font-mono">{pdfFilename}</span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <a
              href={pdfUrl}
              download={pdfFilename}
              className="px-4 py-1.5 text-xs font-semibold bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200 flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" /> Download PDF
            </a>
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors shadow-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
